import { useContext, useEffect } from 'react'
import { Tour, ShepherdButtonWithType, ShepherdOptionsWithType, ShepherdTourContext } from 'react-shepherd'
import UserContext, { Roles } from '../../UserContext'
import { ApiPost } from '../Common/Api'
import { TrackedButton } from '../Common/AnalyticsEvents'
import { ServiceRequestContext } from './ServiceRequestContext'

export const ServiceTourOptions: Tour.TourOptions = {
    defaultStepOptions: {
        cancelIcon: {
            enabled: false
        }
    },
    useModalOverlay: true
}

const buttons: ReadonlyArray<ShepherdButtonWithType> = [
    {
        classes: 'button secondary',
        text: 'Exit',
        type: 'cancel'
    },
    {
        classes: 'button secondary',
        text: 'Back',
        type: 'back'
    },
    {
        classes: 'button default',
        text: 'Next',
        type: 'next'
    }
]

export const ServiceTourSteps: ShepherdOptionsWithType[] = [
    {
        attachTo: { element: '.open-postings', on: 'bottom' },
        buttons: buttons.filter(b => b.type !== 'back'),
        title: 'Open Postings',
        text: ['Clicking here will allow you to see service postings you have not responded to yet.'],
        beforeShowPromise: () => ApiPost('User/Tour?type=service')
    },
    {
        attachTo: { element: '.my-responses', on: 'bottom' },
        buttons,
        title: 'My Responses',
        text: ['Clicking here will allow you to see service postings for which you have responded already. You may also adjust your responses here.'],
    },
    {
        attachTo: { element: '.awarded', on: 'bottom' },
        buttons,
        title: 'Awarded',
        text: ['Clicking here will allow you to see service postings for which you have awarded.'],
    },
    {
        attachTo: { element: '.filter-button', on: 'bottom' },
        buttons,
        title: 'Filtering',
        text: 'You can filter down the services you\'re looking to perform by clicking here'
    },
    {
        attachTo: { element: 'thead tr:first-child th:first-child', on: 'bottom' },
        buttons,
        title: 'Sorting',
        text: 'You can quickly organize bids at anytime clicking columns headers with a <i class="fas fa-sort"></i> on it.'
    },
    {
        attachTo: { element: 'tbody tr:first-child', on: 'bottom' },
        buttons,
        title: 'Service',
        text: ['This shows you the basic information for a service. Clicking on this row will give you more indepth details on the service being requested so you can make an informed decision'],
    },
    {
        attachTo: { element: 'tbody tr:first-child .response-actions-container', on: 'bottom' },
        buttons,
        title: 'Responding to a Service',
        text: '<p>Once you feel ready to respond to a service you can click <i class=\'fas fa-check-circle\'></i> or <i class=\'fas fa-times-circle\'></i></p>' +
            '<p><i class=\'fas fa-check-circle\'></i> indicates you are willing to service this member. We\'ll reach out to you via email if you\'re selected.</p>' +
            '<p><i class=\'fas fa-times-circle\'></i> indicates you are not willing or able to perform the service. To better serve you and our members we\'ll ask why you didn\'t want to do the service. Please select whatever option is most applicable to you.</p>' +
            '<p>Either option will become colored once you make a selection and you can always comeback and change your mind by returning to the response in the <b>My Responses</b> tab</p>'
    },
    {
        attachTo: { element: '.tour-btn', on: 'bottom' },
        buttons: buttons.filter(b => b.type !== 'cancel').map(b => b.type === 'next' ? { ...b, text: 'Finish' } : b),
        title: 'We Got You',
        text: 'If you ever need clarification on what we just went over you can always come back to this tour by clicking here.'
    }
]

const ServiceTourButton: React.FC = () => {
    const tour = useContext(ShepherdTourContext)
    const { hasAnyRole, user, setUser } = useContext(UserContext)
    const { filterValues, services } = useContext(ServiceRequestContext)

    useEffect(() => {
        if (!hasAnyRole(Roles.Provider) || user.serviceTourViewed || !services.length) return

        if (tour?.start) {
            tour.start()
            setUser({ ...user, serviceTourViewed: true })
        }
    }, [user, tour, hasAnyRole, setUser, services])

    if (!hasAnyRole(Roles.Provider) || !['Open', 'Responses'].includes(filterValues.filterGroup || '') || !services.length) return null

    return <div style={{ marginTop: '1.3em' }}><TrackedButton event='TakeTour'><button className='button primary small tour-btn' onClick={() => tour?.start()}> Take a Tour </button></TrackedButton></div>
}

export default ServiceTourButton
